import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const buttonVariants = cva(
	// Base styles
	"inline-flex items-center justify-center rounded-md font-medium font-roboto transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
	{
		variants: {
			variant: {
				primary: "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:ring-ring",
				secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:ring-ring",
				destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 focus-visible:ring-destructive",
				outline: "border border-input bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring",
				ghost: "text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring",
				link: "text-primary underline-offset-4 hover:underline focus-visible:ring-ring"
			},
			size: {
				sm: "h-7 px-2.5 text-sm",
				md: "h-9 px-3.5 text-base",
				lg: "h-10 px-5 text-lg",
				xl: "h-12 px-6 text-xl"
			},
			loading: {
				true: "cursor-not-allowed",
				false: ""
			}
		},
		defaultVariants: {
			variant: "primary",
			size: "md",
			loading: false
		}
	}
);

const Button = forwardRef(({
	className,
	variant,
	size,
	loading = false,
	children,
	asChild = false,
	disabled,
	...props
}, ref) => {
	const Comp = asChild ? Slot : 'button';

	return (
		<Comp
			className={cn(buttonVariants({ variant, size, loading, className }))}
			ref={ref}
			disabled={disabled || loading}
			{...props}
		>
			{/* Exactly one child unless a spinner is shown. `asChild` renders through
			    Radix Slot, which calls React.Children.only and throws on anything but a
			    single element, so an unconditional `{cond && ...}` beside `{children}`
			    broke every `asChild` button in the app. */}
			{loading ? (
				<>
					<div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
					{children}
				</>
			) : children}
		</Comp>
	);
});

Button.displayName = "Button";

export { Button, buttonVariants }; 
